/**
 * ============================================================================
 * useAcknowledgeMutation — Phase 6c frontend (CCIE).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/changes/{id}/acknowledge — the CC author / lead
 *          attests they have reviewed every item in the impact set. This is
 *          a single signature step (separate from approval which may be
 *          dual-signed for bulk CCs per CCIE-017).
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';

export interface AcknowledgeInput {
  changeId: string;
  attestationText: string;
  signatureBundleId: string;
}

export interface AcknowledgeResponse {
  change_id: string;
  status: string;
  acknowledged_at_utc: string;
}

export function useAcknowledgeMutation(): UseMutationResult<
  AcknowledgeResponse,
  Error,
  AcknowledgeInput
> {
  return useMutation<AcknowledgeResponse, Error, AcknowledgeInput>({
    mutationFn: ({ changeId, attestationText, signatureBundleId }) =>
      apiFetch<AcknowledgeResponse>(
        `/api/v1/changes/${encodeURIComponent(changeId)}/acknowledge`,
        {
          method: 'POST',
          body: {
            attestation_text: attestationText,
            signature_bundle_id: signatureBundleId,
          },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
